import deepEqual from 'fast-deep-equal'
import type { ReactNode } from 'react'

import { amountToString, gt, rateGt, rateToPercentString, X0, zeroAmount } from '@orus.eu/amount'
import { formatDdMmYyyy } from '@orus.eu/calendar-date'
import { type FeatureBuilderMapWithBeforeAfter, type FeatureBuilderPropsMap } from '@orus.eu/dimensions'
import type { CommitmentV2 } from '@orus.eu/product'
import type { TranslateInSpecificLanguageFunction } from '@orus.eu/translations'
import { isDefinedAndNotNull } from '@orus.eu/utils'

import {
  projectAttributeInQuoteDetailBody,
  useProductsAttributesProjection,
} from '../../../../code-only/hooks/use-product-attributes-projection.ts'
import { useTranslate } from '../../../../code-only/localization/language-context.ts'
import { getProductDisplayName } from '../../backoffice-quote-editor/badges/product-tags.ts'

/**
 * @requires title to be localized inside the builder function
 * @param contentBefore can be undefined when not given in input
 */
export type Feature = {
  title: string
  contentBefore: ReactNode | undefined
  contentAfter: ReactNode
  areBeforeAfterEqual: boolean
}
/**
 * @param after should be used when displaying only one step without before/after comparison
 */
type FeatureBaseProps = {
  before?: Record<string, unknown>
  after: Record<string, unknown>
}
type FeatureBuilder<P extends FeatureBaseProps> = (props: P) => Feature

/**
 * @intentions This ensure that the builder function is typed correctly with before/after keys.
 * And support more in depth type checking of the props.
 */
type FeatureBuilderMap = {
  [K in keyof FeatureBuilderPropsMap]: FeatureBuilder<FeatureBuilderMapWithBeforeAfter[K]>
}

/**
 * @intentions
 * Document with types all the inputs and outputs
 * of the "features" we show in the recapitulative tables
 * (endorsements and quotes).
 *
 * This makes it easier to understand what data you need to provide if you use only a subset of the features.
 * This also makes it easier to reuse and understand features.
 *
 * @usage
 * Supporting a new feature:
 * - add a new key to the `FeatureBuilderPropsMap`
 * - add a new builder function to this object `FeatureBuilderMap`
 *
 * @example
 * // call
 * paymentsFeaturesBuilders['insuranceProduct']({
 *   after: {
 *     product: {
 *       name: 'Product 1',
 *       code: '123',
 *     },
 *   },
 * })
 *
 * // return value
 * {
 *   title: 'Produit d\'assurance',
 *   contentBefore: undefined,
 *   contentAfter: <>{getProductDisplayName(props.after.product, 'partner')}</>,
 * }
 */
export const featureBuilderMap: FeatureBuilderMap = {
  InsuranceProduct: ({ before, after }: FeatureBuilderMapWithBeforeAfter['InsuranceProduct']) => {
    const translate = useTranslate()
    const areBeforeAfterEqual: boolean = (before && after && deepEqual(before, after)) || false
    return {
      title: translate('insurance_product'),
      contentBefore: valueOrEmptyPlaceholder(before?.product, (p) => (
        <>{getProductDisplayName(p, 'partner', translate)}</>
      )),
      contentAfter: areBeforeAfterEqual
        ? noChangeContent()
        : valueOrEmptyPlaceholder(after.product, (p) => <>{getProductDisplayName(p, 'partner', translate)}</>),
      areBeforeAfterEqual,
    }
  },
  PaymentRecurrence: ({ before, after }: FeatureBuilderMapWithBeforeAfter['PaymentRecurrence']) => {
    const translate = useTranslate()
    function getContent(props: FeatureBuilderPropsMap['PaymentRecurrence']): ReactNode {
      const {
        paymentRecurrence,
        formattedCurrentRecurrencePrice,
        forbiddenMonthlyPayment,
        otherRecurrence,
        otherRecurrencePrice,
        currencyPosition,
      } = props
      const translateOptions = currencyPosition ? { currencyPosition } : undefined
      const hasRecurrenceAndPrice =
        isDefinedAndNotNull(paymentRecurrence) && isDefinedAndNotNull(formattedCurrentRecurrencePrice)
      if (!hasRecurrenceAndPrice) {
        return '-'
      }

      const recurrenceLabel = translate(`subscription_funnel_payment_details_recurrence_${paymentRecurrence}`)
      const priceLabel = `${formattedCurrentRecurrencePrice} ${translate(`per_time_unit_${paymentRecurrence}`)}`
      const showOtherRecurrence =
        forbiddenMonthlyPayment !== true &&
        isDefinedAndNotNull(otherRecurrence) &&
        isDefinedAndNotNull(otherRecurrencePrice)
      return (
        <>
          {recurrenceLabel}
          <br />
          <br />
          {priceLabel}
          {showOtherRecurrence && (
            <>
              <br />
              {translate(
                `or_amount_per_time_unit_${otherRecurrence}`,
                {
                  amount: otherRecurrencePrice,
                },
                translateOptions,
              )}
            </>
          )}
        </>
      )
    }

    const areBeforeAfterEqual: boolean = (before && after && deepEqual(before, after)) || false

    return {
      title: translate('subscription_funnel_payment_details'),
      contentBefore: valueOrEmptyPlaceholder(before, getContent),
      contentAfter: areBeforeAfterEqual ? noChangeContent() : getContent(after),
      areBeforeAfterEqual,
    }
  },
  FirstPeriodPrice: ({ before, after }: FeatureBuilderMapWithBeforeAfter['FirstPeriodPrice']) => {
    const translate = useTranslate()
    const getContent = (props: FeatureBuilderPropsMap['FirstPeriodPrice']) => {
      const firstPart = isDefinedAndNotNull(props.firstPeriodPrice)
        ? amountToString(props.firstPeriodPrice, {
            addCurrency: true,
            ...(props.currencyPosition ? { currencyPosition: props.currencyPosition } : {}),
          })
        : '-'
      const partnerPart =
        isDefinedAndNotNull(props.partnerApplicationPrice) && gt(props.partnerApplicationPrice, zeroAmount)
          ? translate(
              'subscription_funnel_payment_detail_including_setup_fee',
              {
                amount: props.partnerApplicationPrice,
              },
              props.currencyPosition ? { currencyPosition: props.currencyPosition } : undefined,
            )
          : undefined
      const installmentsPart =
        isDefinedAndNotNull(props.invoicesAtSignatureCount) && props.invoicesAtSignatureCount > 1
          ? translate('subscription_funnel_payment_detail_first_payment_installments_count', {
              count: props.invoicesAtSignatureCount,
            })
          : undefined
      const content = [[firstPart, installmentsPart].filter(Boolean).join(' '), partnerPart].filter(Boolean).join(', ')
      return content
    }
    const areBeforeAfterEqual: boolean = (before && after && deepEqual(before, after)) || false
    const paymentRecurrenceKey =
      after.paymentRecurrence === 'monthly' || after.paymentRecurrence === 'yearly'
        ? after.paymentRecurrence
        : undefined

    return {
      title: paymentRecurrenceKey
        ? translate(`subscription_funnel_payment_detail_first_payement_${paymentRecurrenceKey}`)
        : '-',
      contentBefore: before ? getContent(before) : undefined,
      contentAfter: areBeforeAfterEqual ? noChangeContent() : getContent(after),
      areBeforeAfterEqual,
    }
  },
  Commitment: ({ before, after }: FeatureBuilderMapWithBeforeAfter['Commitment']) => {
    const translate = useTranslate()
    const getContent = (commitmentV2: CommitmentV2 | null | undefined) =>
      valueOrEmptyPlaceholder(commitmentV2, (c) => getCommitmentText(c, translate))
    const areBeforeAfterEqual: boolean =
      (before && after && deepEqual(before.commitmentV2, after.commitmentV2)) || false

    return {
      title: translate('subscription_funnel_payment_detail_commitment'),
      contentBefore: before ? getContent(before.commitmentV2) : undefined,
      contentAfter: areBeforeAfterEqual ? noChangeContent() : getContent(after.commitmentV2),
      areBeforeAfterEqual,
    }
  },
  Advantage: ({ before, after }: FeatureBuilderMapWithBeforeAfter['Advantage']) => {
    const translate = useTranslate()
    const getContent = (props: FeatureBuilderPropsMap['Advantage']) => {
      const firstYearReduction = props.firstYearReductionRate
        ? translate('subscription_funnel_first_year_reduction_badge', { rate: props.firstYearReductionRate })
        : undefined
      const discountBadge =
        isDefinedAndNotNull(props.discountRate) && rateGt(props.discountRate, X0)
          ? translate('subscription_funnel_discount_badge', { rate: props.discountRate })
          : undefined
      const yearlyDiscount =
        props.yearlyDiscountEnabled === true && props.yearlyCommitmentDiscountRate
          ? translate('subscription_funnel_payment_detail_advantage_discount', {
              discountPercent: rateToPercentString(props.yearlyCommitmentDiscountRate),
            })
          : undefined
      const content = [firstYearReduction, discountBadge, yearlyDiscount].filter(Boolean)

      if (content.length === 0) return '-'
      return content.join(', ')
    }
    const areBeforeAfterEqual: boolean = (before && after && deepEqual(before, after)) || false
    return {
      title: translate('subscription_funnel_payment_detail_advantage'),
      contentBefore: before ? getContent(before) : undefined,
      contentAfter: areBeforeAfterEqual ? noChangeContent() : getContent(after),
      areBeforeAfterEqual,
    }
  },
  Taxes: ({ before, after }: FeatureBuilderMapWithBeforeAfter['Taxes']) => {
    const translate = useTranslate()
    const GetContent = (props: FeatureBuilderPropsMap['Taxes']) => {
      const attributesProjectedInBody = useProductsAttributesProjection(
        props.attributesProjectedInBody ?? [],
        projectAttributeInQuoteDetailBody,
      )
      const attributesCount = props.attributesProjectedInBody?.length ?? 0
      const content = [
        ...attributesProjectedInBody.flatMap(
          ({ productName, attributes }) =>
            `${attributes.map((attribute) => attribute.label).join(', ')}${
              attributesCount > 1 ? ` (${productName})` : ''
            }`,
        ),
        props.operatingZone === 'fr' ? translate('subscription_funnel_payment_detail_no_vat') : undefined,
        props.hasTerrorismTax === true && isDefinedAndNotNull(props.yearlyTerrorismTaxPrice)
          ? translate('subscription_funnel_payment_detail_yearly_terrorism_tax', {
              amount: props.yearlyTerrorismTaxPrice,
            })
          : undefined,
      ]
        .filter(Boolean)
        .join(', ')
      return content || '-'
    }
    const areBeforeAfterEqual: boolean = (before && after && deepEqual(before, after)) || false

    return {
      title: translate('subscription_funnel_payment_detail_taxes'),
      contentBefore: before ? GetContent(before) : undefined,
      contentAfter: areBeforeAfterEqual ? noChangeContent() : GetContent(after),
      areBeforeAfterEqual,
    }
  },
}

function noChangeContent(): ReactNode {
  return <>✓</>
}

/**
 * Renders a value or '-' when it is null or undefined.
 * null = we explicitly set the value to null; undefined = not set yet.
 * Both render as '-' for now; the distinction is kept for possible different rendering later.
 */
function valueOrEmptyPlaceholder<T>(value: T | null | undefined, render: (v: T) => ReactNode): ReactNode {
  if (!isDefinedAndNotNull(value)) {
    return '-'
  }
  return render(value)
}

/**
 * The two other occurences
 */
export function getCommitmentText(
  commitmentV2Current: CommitmentV2,
  translate: TranslateInSpecificLanguageFunction,
): string {
  switch (commitmentV2Current.type) {
    case 'no-commitment':
      return translate('certificate_no_commitment')
    case 'rolling-period':
      switch (commitmentV2Current.recurrence) {
        case '1-month':
          return translate('commitment_monthly')
        case '1-year':
          return translate('commitment_yearly')
      }
      break
    case 'fixed-term':
      switch (commitmentV2Current.termDuration) {
        case '1-year':
          return translate('certificate_commitment_fixed_term_1_year', {
            endDate: formatDdMmYyyy(commitmentV2Current.endDate),
          })
      }
      break
  }
  return translate('certificate_no_commitment')
}
